"use client";

import * as React from "react";
import type { Table } from "@tanstack/react-table";
import { ChevronDown, ChevronDownIcon } from "lucide-react";

import {
  TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS,
  TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS,
} from "@/components/trade-filter-selects";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";

/** Column visibility — shadcn data-table docs (DropdownMenu + CheckboxItem). */
export function DataTableColumnVisibility<TData>({
  table,
  menuContentProps,
  tradeLogTypography = false,
}: {
  table: Table<TData>;
  menuContentProps?: React.ComponentProps<typeof DropdownMenuContent>;
  /** Trade Log toolbar: primary font + gray-500 on Columns menu items. */
  tradeLogTypography?: boolean;
}) {
  const { className: menuClassName, ...restMenuContentProps } = menuContentProps ?? {};

  const menu = (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        {tradeLogTypography ? (
          <button
            type="button"
            role="combobox"
            data-slot="select-trigger"
            data-size="default"
            data-trade-log-columns-trigger=""
            className={cn(
              TRADE_LOG_COLUMNS_SELECT_TRIGGER_CLASS,
              "min-w-[10rem]",
            )}
          >
            <span
              data-slot="select-value"
              className="dark:text-sidebar-muted-foreground"
            >
              Columns
            </span>
            <ChevronDownIcon className="size-4 shrink-0 text-muted-foreground dark:text-sidebar-muted-foreground" />
          </button>
        ) : (
          <Button
            data-table-control
            data-font="primary"
            type="button"
            variant="outline"
            className="h-9"
          >
            Columns
            <ChevronDown />
          </Button>
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        side="bottom"
        avoidCollisions={false}
        className={cn(
          "w-auto min-w-40 max-w-[min(90vw,32rem)]",
          menuClassName,
        )}
        {...(tradeLogTypography
          ? { "data-trade-log-columns-menu": "", "data-field-select-menu": "" }
          : {})}
        {...restMenuContentProps}
      >
        {table
          .getAllColumns()
          .filter((column) => column.getCanHide())
          .map((column) => {
            const label =
              (column.columnDef.meta as { label?: string } | undefined)?.label ??
              column.id;

            return (
              <DropdownMenuCheckboxItem
                key={column.id}
                className={TRADE_LOG_COLUMNS_MENU_CHECKBOX_ITEM_CLASS}
                checked={column.getIsVisible()}
                onCheckedChange={(value) => column.toggleVisibility(!!value)}
              >
                {label}
              </DropdownMenuCheckboxItem>
            );
          })}
      </DropdownMenuContent>
    </DropdownMenu>
  );

  if (tradeLogTypography) {
    return (
      <div data-field-select="" data-trade-log-columns-control="" className="w-fit">
        {menu}
      </div>
    );
  }

  return menu;
}
